import { IModel, Collection, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { SortExpression, ISortableTransformation } from './sort-expression';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { BaseGroupByTransformation } from './base-group-by-transformation';
import { Owned } from '../base/named';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { DataFlowFunctionArgument } from 'app/model/dataflow/dataflow-function-argument';
import { ExpressionBasedColumn } from './expression-based.column';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { Dictionary } from 'lodash';
import { DataFlowExpression } from './dataflowexpression';
import { ExpressionBasedColumnPattern } from './expression-based.column.pattern';
import { BaseSchemaModifierTransformation } from './base-schema-modifier-transformation';

export interface IRangeValue {
    dateValues?: number[];
    longValue?: number;
}

@Metamodel.model({
    name: 'window',
    display: [stringResources.DataFlowWindowTransformationLabel, stringResources.DataFlowWindowTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        },
        transientColumns: {
            validator: BaseSchemaModifierTransformation.validateTransientColumns
        }
    }
})
export class WindowTransformation extends BaseGroupByTransformation implements ISortableTransformation {
    public static readonly MODEL: IModel;
    public static readonly UNBOUNDED: string = 'Unbounded';
    public static readonly longRegex: RegExp = /^(-)?([0-9]+)L$/;
    public static readonly funcRegex: string = '^(.*[ \\+\\(])?{0}\\(([0-9]+)\\)';
    // multipliers for converting date values to seconds
    private static readonly rangeDateMultipliers: Dictionary<number> = {
        weeks: 7 * 24 * 60 * 60,
        days: 24 * 60 * 60,
        hours: 60 * 60,
        minutes: 60,
        seconds: 1,
        milliseconds: 0.001
    };

    @Metamodel.attribute({
        display: stringResources.WindowTransformationOverColumn,
        type: ExpressionBasedColumn.MODEL,
        collection: Collection.Array,
        constraint: {
            unique: true,
            minOccurs: 0
        }
    })
    public groupByColumns: ExpressionBasedColumn[] = [];

    @Metamodel.attribute({
        display: stringResources.WindowTransformationWindowColumn,
        constraint: {
            minOccurs: 1,
            validator: WindowTransformation.validateWindowColumns
        }
    })
    public expressionColumns: ExpressionBasedColumnBase[] = [];

    @Metamodel.attribute({
        type: SortExpression.MODEL,
        collection: Collection.Array,
        constraint: {
            unique: true,
            validator: WindowTransformation.validateSortExpressions
        }
    })
    public sortExpressions: SortExpression[] = [];

    public rangeByOffset: boolean = true;

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            validator: WindowTransformation.validateRange
        }
    })
    public range: string[] = [WindowTransformation.UNBOUNDED, WindowTransformation.UNBOUNDED];

    public rangeSpecified: boolean = false;

    public constructor() {
        super();
        this.inspectSummaryOutputFields.splice(1, 0, {
            name: () => stringResources.TransformationInspect_Update,
            icon: () => 'svg-i-9x9-Update',
            value: () => this.overwrittenColumns.length
        });
        this.inspectDetailsOutputFields.unshift({
            name: () => this.columnInfoTitle,
            icon: this._getUpdatedIcon.bind(this)
        });
    }

    public static validateSortExpressions(transformation: WindowTransformation): IViolation[] {
        let violations: IViolation[] = [];
        if (transformation.range[0] !== WindowTransformation.UNBOUNDED || transformation.rangeSpecified
            || transformation.range[1] !== WindowTransformation.UNBOUNDED || transformation.sortExpressions.length > 1) {
            violations = SortExpression.validateExpressions(transformation);
        }
        return violations;
    }

    public static validateWindowColumns(transformation: WindowTransformation): IViolation[] {
        const violations: IViolation[] = [];
        const duplicateColumns = new Set<string>();
        transformation.expressionColumns.filter((column: ExpressionBasedColumn) => !!column.column).forEach((column: ExpressionBasedColumn) => {
            if (transformation.sortExpressions.find((sort) => sort.dataFlowExpression.expression === column.column.name)) {
                duplicateColumns.add(column.column.name);
            }
            if (transformation.groupByColumns.find((groupByColumn) => groupByColumn.column.name === column.column.name)) {
                duplicateColumns.add(column.column.name);
            }
        });
        duplicateColumns.forEach((column) => {
            violations.push({
                context: transformation,
                message: () => sanitizeAndFormatString(stringResources.WindowTransformationDuplicateColumns, column)
            });
        });
        return violations;
    }

    public get columns(): DataFlowColumn[] {
        const windowColumns = ColumnExpressionProcessor.processNewColumns(this.expressionColumns, this.distinctInputColumnRefs, this)
            .map((processedColumnResult) => processedColumnResult.column);
        const columns: DataFlowColumn[] = [];
        this.overwrittenColumns = [];
        this.newColumns = [];
        this.unchangedColumns = [];
        for (const column of this.inputColumns) {
            const overwrittenColumnIndex = windowColumns.findIndex((windowColumn) => (windowColumn.name) === column.name);
            if (overwrittenColumnIndex > -1) {
                const overwrittenColumn = windowColumns[overwrittenColumnIndex];
                if (columns.indexOf(overwrittenColumn) < 0) {
                    columns.push(overwrittenColumn);
                }
                if (this.overwrittenColumns.indexOf(overwrittenColumn) < 0) {
                    this.overwrittenColumns.push(overwrittenColumn);
                }
            } else {
                this.unchangedColumns.push(column);
                columns.push(column);
            }
        }

        for (const column of windowColumns) {
            if (columns.indexOf(column) < 0) {
                columns.push(column);
                if (this.overwrittenColumns.indexOf(column) < 0) {
                    this.newColumns.push(column);
                }
            }
        }
        return columns;
    }

    public static validateRange(transformation: WindowTransformation): IViolation[] {
        const violations: IViolation[] = [];
        if (!transformation.range[0] || !transformation.range[1]) {
            violations.push({
                context: transformation,
                message: () => stringResources.WindowTransformationNoRange
            });
        } else {
            if (WindowTransformation.checkStartRange(transformation)) {
                violations.push({
                    context: transformation,
                    message: () => stringResources.WindowTransformationInvalidRange
                });
            }
        }
        return violations;
    }

    public static getNumberFromRange(input: string): IRangeValue {
        const multiplier = input.startsWith('-') ? -1 : 1;
        const values: number[] = [];
        let containsDate: boolean = false;
        const rangeValue: IRangeValue = {};
        Object.keys(WindowTransformation.rangeDateMultipliers).forEach((func) => {
            const fRegex: RegExp = new RegExp(format(WindowTransformation.funcRegex, func));
            if (fRegex.test(input)) {
                const matches = fRegex.exec(input);
                if (matches && matches.length) {
                    containsDate = true;
                    values.push(Number(matches[matches.length - 1]) * multiplier);
                }
            } else {
                values.push(Number.NaN);
            }
        });
        if(containsDate) {
            rangeValue.dateValues = values;
        }
        if (!containsDate && WindowTransformation.longRegex.test(input)) {
            const matches = WindowTransformation.longRegex.exec(input);
            const result = matches && matches.length ? Number(matches[matches.length - 1]) * multiplier : 0;
            rangeValue.longValue = result;
        }
        return rangeValue;
    }

    private static checkStartRange(transformation: WindowTransformation): boolean {
        const startRange = transformation.range[0];
        const endRange = transformation.range[1];
        if ((startRange && startRange !== WindowTransformation.UNBOUNDED) && (endRange && endRange !== WindowTransformation.UNBOUNDED)) {
            const startNumber = WindowTransformation.getNumberFromRange(startRange);
            const endNumber = WindowTransformation.getNumberFromRange(endRange);
            if (WindowTransformation.normalizeRangeValues(startNumber) > WindowTransformation.normalizeRangeValues(endNumber)) {
                return true;
            }
        }
        return false;
    }

    private static normalizeRangeValues(input: IRangeValue): number {
        if (input.dateValues) {
            let result = 0;
            const multipliers = Object.keys(WindowTransformation.rangeDateMultipliers).map((key) => WindowTransformation.rangeDateMultipliers[key]);
            input.dateValues.forEach((value, index) => {
                result += value * multipliers[index];
            });
            return result;
        }
        return input.longValue;
    }

    public addSortExpression(): SortExpression {
        const sortExpression = Owned.create<SortExpression>(SortExpression.MODEL, this);
        sortExpression.ascending = true;
        sortExpression.nullsFirst = true;
        this.sortExpressions.push(sortExpression);
        return sortExpression;
    }

    public addColumn(columnPattern?: ExpressionBasedColumnPattern, index?: number): ExpressionBasedColumn {
        const expColumn = super.addColumn(columnPattern, index);
        const requiredFunctions = [].concat(DataFlowExpressionFunctions.WINDOW_FUNCTIONS, DataFlowExpressionFunctions.AGGREGATE_FUNCTIONS);
        expColumn.dataFlowExpression.setRequiredFunctions(requiredFunctions, stringResources.NoWindowFunctionUsedError);
        return expColumn;
    }

    public getExpressions(): DataFlowExpression[] {
        const dataFlowExpressions: DataFlowExpression[] = super.getExpressions();
        this.sortExpressions.forEach((sortExpression) => {
            if (sortExpression.dataFlowExpression) {
                dataFlowExpressions.push(sortExpression.dataFlowExpression);
            }
        });

        return dataFlowExpressions;
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
        SortExpression.updateSortExpressionIdentifiers(this.sortExpressions, this.distinctInputColumnRefs, this.dataflow.parameters, this.dataflow.cacheSinks, this);
    }

    protected getGeneratedDescription(): string {
        return stringResources.WindowTransformationDescription;
    }

    protected getAvailableFunctionsForExpressionColumns(): DataFlowFunctionArgument[] {
        return DataFlowExpressionFunctions.ALL_INLCUDING_WINDOW_FUNCTIONS;
    }
}
